// src/pages/staff/CreateStaffPage.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import StaffForm from '../components/StaffForm';

// Hooks
import { useStaff } from '../hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';

// Utils
import { generateRandomPassword } from '../../../utils/random';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateStaffPage() {
  const { t } = useTranslation(['staff', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showError } = useSnackbar();

  // State
  const [formData, setFormData] = useState({ name: '', hire_date: null });
  const [validationErrors, setValidationErrors] = useState({});
  const [createdPassword, setCreatedPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // API hooks
  const { createStaff, loading, error, clearError } = useStaff();

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('staff:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('staff:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('staff:validation.name_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const password = generateRandomPassword(8, '0123456789');

    try {
      const result = await createStaff({ ...formData, password });

      if (result.success) {
        setCreatedPassword(password);
      } else {
        showError(result.error || t('staff:errors.create_failed'));
      }
    } catch {
      showError(t('staff:errors.unexpected_error'));
    }
  }, [validateForm, formData, createStaff, showError, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/staff');
  }, [navigateBack]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(createdPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [createdPassword]);

  const handleDismiss = useCallback(() => {
    navigate('/staff');
  }, [navigate]);

  return (
    <>
      <FormPage
        title={t('staff:add_staff_member')}
        subtitle={t('staff:form.create_subtitle')}
        onBack={handleCancel}
        onSubmit={handleSubmit}
        fieldErrors={validationErrors}
        error={error}
        onErrorClose={clearError}
      >
        <StaffForm
          formData={formData}
          onInputChange={handleInputChange}
          validationErrors={validationErrors}
          loading={loading}
          mode="create"
        />
        <FormActions
          onCancel={handleCancel}
          submitType="submit"
          submitLabel={loading ? t('staff:actions.creating') : t('staff:actions.create_staff_member')}
          submitIcon={<SaveIcon />}
          loading={loading}
        />
      </FormPage>

      {/* Password dialog shown after successful creation */}
      <Dialog open={!!createdPassword} maxWidth="xs" fullWidth>
        <DialogTitle>{t('staff:success.staff_created')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Typography variant="body2">
              {t('staff:profile.reset_password_instructions')}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderRadius: 1,
              }}
            >
              <Box display="flex" gap={1}>
                {createdPassword.match(/.{1,2}/g)?.map((chunk, i) => (
                  <Typography key={i} variant="h6" fontWeight="bold" letterSpacing={2}>
                    {chunk}
                  </Typography>
                ))}
              </Box>
              <IconButton onClick={handleCopy} size="small" color={copied ? 'success' : 'default'}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleDismiss}>
            {t('common:buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
